import {type PaymentRequestOptions, type PaymentRequestUpdateOptions, type PaymentRequest, type PaymentRequestPaymentMethodEvent} from '@stripe/stripe-js';
import {FeatureFlag} from '../../../../@type/features';
import {type TokenResult} from '../../../../@type/stripe';
import {Feature} from '../../reducers/environmentReducer';
import {MerchantConfiguration} from '../../reducers/merchantConfigurationReducer';
import {store} from '../../store';
import {getLocaleText} from '../../util/translation';

const SUPPORTED_ZERO_DECIMAL_CURRENCIES = [
	'BIF',
	'CLP',
	'DJF',
	'GNF',
	'JPY',
	'KMF',
	'KRW',
	'MGA',
	'PYG',
	'RWF',
	'UGX',
	'VND',
	'VUV',
	'XAF',
	'XOF',
	'XPF',
];

function createPaymentRequestOptions(): PaymentRequestOptions {
	const {cart} = store.getState().calculatedCarts[0];
	const {summary} = store.getState().calculatedCarts[0];

	const data: PaymentRequestOptions = {
		country: Feature.metadata<string>(FeatureFlag.STRIPE_GATEWAYS, 'country') ?? 'US',
		currency: MerchantConfiguration.currency.code().toLowerCase(),
		total: {
			label: getLocaleText('Total'),
			amount: formatCurrency(summary.total, MerchantConfiguration.currency.code()),
		},
		displayItems: cart.map(l => ({
			label: l.name,
			amount: formatCurrency(Number.parseFloat(l.total) * Number.parseInt(l.quantity, 10), MerchantConfiguration.currency.code()),
		})),
	};

	return data;
}

function updatePaymentRequestOptions(): PaymentRequestUpdateOptions {
	const {cart} = store.getState().calculatedCarts[0];
	const {summary} = store.getState().calculatedCarts[0];

	const data: PaymentRequestUpdateOptions = {
		currency: MerchantConfiguration.currency.code().toLowerCase(),
		total: {
			label: getLocaleText('Total'),
			amount: formatCurrency(summary.total, MerchantConfiguration.currency.code()),
		},
		displayItems: cart.map(l => ({
			label: l.name,
			amount: formatCurrency(Number.parseFloat(l.total) * Number.parseInt(l.quantity, 10), MerchantConfiguration.currency.code()),
		})),
	};

	return data;
}

function tokenizePaymentRequest(paymentRequest: PaymentRequest): Promise<TokenResult> | TokenResult {
	return new Promise(resolve => {
		paymentRequest.once('paymentmethod', (event: PaymentRequestPaymentMethodEvent) => {
			paymentRequest.off('cancel');

			event.complete('success');
			resolve([event.paymentMethod.id, {}]);
		});

		paymentRequest?.once('cancel', () => {
			paymentRequest.off('paymentmethod');

			resolve([null, {
				note: 'Payment was canceled by customer',
			}]);
		});

		paymentRequest.show();
	});
}

function formatCurrency(amount: string | number, currencyCode: string): number {
	let total = Number.parseFloat(String(amount));

	if (!SUPPORTED_ZERO_DECIMAL_CURRENCIES.includes(currencyCode)) {
		total *= 100;
	}

	return Math.round(total);
}

export {createPaymentRequestOptions, tokenizePaymentRequest, updatePaymentRequestOptions};
